<script setup>
import { computed, nextTick, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import { useAlert } from 'dashboard/composables';
import { useMapGetter } from 'dashboard/composables/store';
import { frontendURL, conversationUrl } from 'dashboard/helper/URLHelper.js';
import MessageList from 'dashboard/components-next/message/MessageList.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import TeleportWithDirection from 'dashboard/components-next/TeleportWithDirection.vue';
import { useFocusTrap } from '../composables/useFocusTrap';
import {
  useConversationThread,
  ATTACHMENT_FILTER_OPTIONS,
} from '../composables/useConversationThread';
import { contactCardIdentity } from '../helpers/kanbanContact';
import KanbanDebthubLinkPanel from './KanbanDebthubLinkPanel.vue';
import KanbanDebthubDetailPanel from './KanbanDebthubDetailPanel.vue';
import KanbanConversationSummary from './KanbanConversationSummary.vue';

// Ler a conversa sem SAIR do board: navegar para a rota da conversa
// descarrega a página inteira do funil (filtros, posição de rolagem, coluna
// em foco) para responder a uma pergunta de dois segundos — "o que essa
// pessoa disse?". O modal responde por cima do board e o devolve intacto.
//
// Somente leitura: responder continua sendo trabalho da tela da conversa,
// que tem o editor, os anexos, os templates e o resto. O caminho para lá
// fica explícito no cabeçalho deste diálogo.
const props = defineProps({
  card: { type: Object, required: true },
});

const emit = defineEmits(['close']);

const { t } = useI18n();
const route = useRoute();
const router = useRouter();
const currentUserId = useMapGetter('getCurrentUserID');

const conversation = computed(() => props.card.conversation);
const identity = computed(() =>
  contactCardIdentity(conversation.value.contact)
);

const dialogRef = ref(null);
const close = () => emit('close');

// A armadilha de foco mede o MESMO dialogRef que agora também envolve o
// painel de detalhe (ver template): abrir o painel amplia o conjunto
// focável automaticamente, sem precisar tocar useFocusTrap.js — o seletor
// é reavaliado a cada Tab.
useFocusTrap(dialogRef, { onEscape: close });

// EXPANSÃO LATERAL de detalhe (candidato em avaliação ou vínculo
// confirmado, nunca os dois): decisão 1 do PRD é que ela NUNCA soma uma
// chamada de rede à abertura do diálogo — o `subject` que chega aqui já é o
// candidato/link que useDebthubLink carregou dentro de KanbanDebthubLinkPanel.
// `detailTrigger` guarda o botão que abriu o painel (fora do estado
// reativo: é usado só imperativamente, para devolver o foco a ele quando o
// painel fecha) e não precisa disparar re-render.
const detailRequest = ref(null);

// Painel do RESUMO, na lateral ESQUERDA — espelho do painel de detalhe do
// DEBTHUB, do outro lado. Enquanto o resumo era uma faixa no fluxo da coluna
// principal, um resumo longo empurrava a thread até o piso de 128px dela e a
// conversa sumia (relatado em produção em 2026-08-20).
const isSummaryOpen = ref(false);
const toggleSummary = () => {
  isSummaryOpen.value = !isSummaryOpen.value;
};

// A largura do diálogo escala com QUANTOS painéis estão abertos, e o que sobra
// vai para a conversa (os painéis têm largura fixa). Com os dois abertos e o
// teto anterior de 6xl (1152px), a conversa ficava com 1152-400-400=352px —
// coluna de leitura mais estreita que os painéis que a ladeavam, num viewport
// de 1951px com ~810px sobrando sem uso (medido no relato de 2026-08-20).
//
// Os saltos são por breakpoint porque a largura DISPONÍVEL é do viewport: um
// teto de 92rem em 1280px não caberia. Em 2xl (>=1536px) 92rem = 1472px deixa
// 32px de folga para o padding do overlay, e devolve ~672px à conversa.
const painelAbertos = computed(
  () => (detailRequest.value ? 1 : 0) + (isSummaryOpen.value ? 1 : 0)
);
const dialogWidthClass = computed(() => {
  if (painelAbertos.value >= 2) {
    return 'lg:max-w-6xl xl:max-w-[85rem] 2xl:max-w-[92rem]';
  }
  if (painelAbertos.value === 1) {
    return 'lg:max-w-5xl xl:max-w-6xl 2xl:max-w-7xl';
  }
  return '';
});
let detailTrigger = null;

const openDetail = ({ subject, kind, trigger }) => {
  detailTrigger = trigger;
  detailRequest.value = { subject, kind };
};

// Chave que identifica o assunto exibido no painel (candidato OU vínculo —
// os dois payloads carregam `pessoa_id`, ver Debt::DebthubLinkResolver), para
// KanbanDebthubLinkPanel saber qual gatilho está "expandido" (D7) e detectar
// quando o próprio assunto deixou de existir na lista (D3, abaixo).
const detailOpenKey = computed(
  () => detailRequest.value?.subject?.pessoa_id ?? null
);

const closeDetail = () => {
  detailRequest.value = null;
  // `detailTrigger` é um nó DOM guardado fora do estado reativo. Se o
  // candidato dono dele foi descartado enquanto o painel estava aberto
  // (ver @invalidate-details abaixo), o nó já foi desmontado por essa
  // altura — `.focus()` nele é no-op silencioso e o foco cai em <body>,
  // fora do diálogo. `isConnected` distingue os dois casos; o fallback é o
  // próprio diálogo (mesmo alvo que a armadilha de foco usa quando não há
  // nenhum elemento focável).
  if (detailTrigger?.isConnected) {
    detailTrigger.focus();
  } else {
    dialogRef.value?.focus();
  }
  detailTrigger = null;
};

// Id derivado da conversa: o diálogo é remontado a cada card aberto e o
// aria-labelledby não pode apontar para um id que sobreviveu ao anterior.
const titleId = computed(
  () => `kanban-conversation-modal-title-${conversation.value.id}`
);

const conversationPath = computed(() =>
  frontendURL(
    conversationUrl({
      accountId: route.params.accountId,
      id: conversation.value.id,
    })
  )
);

// <a> de verdade, não um botão: assim Ctrl/Cmd+clique e o botão do meio
// abrem a conversa em aba nova pelo próprio navegador, sem código, e o
// leitor de tela anuncia "link". O clique simples é interceptado para
// navegar pelo roteador em vez de recarregar a aplicação inteira.
const onOpenFullConversation = event => {
  if (event.metaKey || event.ctrlKey || event.shiftKey) return;
  event.preventDefault();
  router.push({ path: conversationPath.value });
};

const isEmailChannel = computed(
  () => conversation.value.inbox?.channel_type === 'Channel::Email'
);

// conversation.id do card é o display_id (ver _card.json.jbuilder), que é
// exatamente o que a rota de mensagens espera.
const {
  visibleMessages,
  loadedCount,
  visibleCount,
  isLoading,
  hasError,
  isLoadingOlder,
  hasOlderError,
  hasReachedStart,
  searchQuery,
  attachmentFilter,
  isFiltering,
  loadLatest,
  loadOlder,
  resetFilters,
  isLoaded,
} = useConversationThread(() => conversation.value.id);

const scrollRef = ref(null);

// A prévia do card mostra a mensagem MAIS RECENTE, e é ela que leva o agente
// a abrir o diálogo — abrir na posição padrão do scroll (topo) mostra a mais
// ANTIGA carregada, a centenas de pixels de distância da mensagem que
// motivou o clique (medido no app real: scrollTop=0, scrollHeight=1998,
// clientHeight=582 — 1.416px de distância). O mesmo `scrollToBottom` que
// MessagesView.vue já faz ao montar (:347) resolve a posição inicial.
//
// scrollTop=0 ao abrir deixava o diálogo permanentemente dentro do limiar de
// 100px que dispara carregar histórico anterior (`onThreadScroll`, abaixo) —
// medido: um gesto de roda de 40px já teletransportava scrollTop de 40 para
// 1868, carregando 20 mensagens que ninguém pediu. O `scrollToBottom` por si
// só NÃO fecha esse gatilho em toda conversa: o fecho depende de DOIS
// mecanismos, e cada um cobre um caso diferente.
//   - Conversa longa: o transbordo é grande e o pouso do scroll fica bem
//     acima do limiar (medido acima: 1.416px de distância).
//   - Conversa curta: o transbordo pode ficar DENTRO do limiar (medido: 52px
//     numa conversa curta) — quem fecha o gatilho aqui não é o scroll, é
//     `hasReachedStart`. Uma primeira página com menos que
//     `MESSAGES_PAGE_LIMIT` itens já marca `hasReachedStart = true`
//     (useConversationThread.js:134), e `loadOlder` retorna cedo por causa
//     disso (useConversationThread.js:142-150) — o scroll dispara o gatilho,
//     mas não pede nada.
// CASO RESIDUAL, não fechado por nenhum dos dois: 20+ mensagens curtas cujo
// transbordo total fica abaixo de 100px. A primeira página vem CHEIA
// (`hasReachedStart` continua false — sem sinal de fim) e o pouso do
// `scrollToBottom` cai dentro do limiar: o scroll dispara
// `loadOlderKeepingPosition` sem que o agente tenha pedido. Não fechado
// nesta mudança.
//
// Diferente de MessagesView.vue, esta lista não marca mensagem como
// lida/não lida (é histórico somente-leitura, sem contagem por mensagem —
// só `unread_count` agregado no card), então não há "primeira não lida"
// para priorizar aqui: rola direto para o fim.
const scrollToBottom = () => {
  const container = scrollRef.value;
  if (!container) return;
  container.scrollTop = container.scrollHeight;
};

onMounted(async () => {
  await loadLatest();
  await nextTick();
  scrollToBottom();
});

// Prepender mensagens empurra para baixo o trecho que a pessoa estava lendo;
// devolver a diferença de altura mantém o olho onde ele estava, e é o mesmo que
// MessagesView.vue faz na tela cheia da conversa.
const loadOlderKeepingPosition = async () => {
  const container = scrollRef.value;
  const heightBefore = container?.scrollHeight ?? 0;
  const topBefore = container?.scrollTop ?? 0;

  await loadOlder();
  await nextTick();

  if (!container) return;
  container.scrollTop = topBefore + (container.scrollHeight - heightBefore);
};

// Rolar para o topo carrega a página anterior — mesmo gesto e mesmo limiar de
// 100px da tela cheia da conversa. O botão explícito NÃO é redundante: quando
// as 20 mensagens da última página não enchem o diálogo não existe rolagem
// nenhuma para escutar, e teclado e leitor de tela não têm gesto de rolagem.
const onThreadScroll = () => {
  if ((scrollRef.value?.scrollTop ?? 0) < 100) loadOlderKeepingPosition();
};

// O que a tela é obrigada a dizer: a busca só alcança o que ESTÁ carregado.
// Não existe busca de mensagem por conversa no servidor (SearchService é por
// CONTA, com piso de 3 meses e 15 por página), então uma busca que não achasse
// nada poderia significar "não existe" ou "ainda não carregou" — e calar essa
// diferença faria o agente concluir a primeira.
//
// `hasReachedStart` NÃO É "o histórico acabou": é "esta página voltou com
// menos itens do que o servidor devolveria se houvesse mais" — vazia ou
// parcial, o sinal é o mesmo (useConversationThread, MESSAGES_PAGE_LIMIT).
// `MessageFinder#messages_before` filtra por `id < ?` mas ordena por
// `created_at`, e nesta base as duas ordens divergem em 2.111 das 199.411
// conversas (1,06%, medido) — nelas uma página pode vir curta com mensagens
// mais antigas por `created_at` (não por id) ainda por carregar. Por isso as
// duas chaves ligadas a este sinal (HISTORY_START, SCOPE_COMPLETE) só podem
// afirmar o que é literalmente verdade — "nada mais volta por este
// id-cursor" —, nunca que o histórico terminou nem que a busca cobre a
// conversa inteira.
// Pluralização de verdade (vue-i18n `count | count`, mesmo padrão de
// PaginationFooter.vue): sem ela "1 messages loaded" / "1 mensagens
// carregadas" saía a cada tecla digitada na busca, numa região `role="status"`
// que o leitor de tela anuncia.
const threadStatusLabel = computed(() => {
  const counts = isFiltering.value
    ? t(
        'KANBAN.BOARD.CONVERSATION_MODAL.RESULT_COUNT',
        { visible: visibleCount.value, loaded: loadedCount.value },
        Number(loadedCount.value)
      )
    : t(
        'KANBAN.BOARD.CONVERSATION_MODAL.LOADED_COUNT',
        { loaded: loadedCount.value },
        Number(loadedCount.value)
      );
  const scope = hasReachedStart.value
    ? t('KANBAN.BOARD.CONVERSATION_MODAL.SCOPE_COMPLETE')
    : t('KANBAN.BOARD.CONVERSATION_MODAL.SCOPE_PARTIAL');

  return `${counts} ${scope}`;
});

// Âncora da citação do resumo: a garantia da citação é o agente PODER
// conferir o trecho na mensagem de onde ele saiu, e citação sem link não
// entrega isso. Message.vue (components-next) já expõe data-message-id no
// elemento raiz de cada bolha — a busca é escopada à thread DESTE diálogo, e
// não ao documento, porque a tela cheia da conversa usa os mesmos ids.
const threadRef = ref(null);

const goToCitedMessage = async messageId => {
  // O message_id atravessa dois sistemas vindo da SAÍDA DE UM MODELO. Só um
  // id numérico vira seletor: qualquer outra coisa faria querySelector
  // levantar SyntaxError dentro do diálogo.
  const id = String(messageId ?? '');

  // A mensagem citada pode estar CARREGADA e escondida pelo filtro. Dizer
  // "não está carregada" nesse caso seria falso, e um clique que não faz nada
  // seria pior: limpar o filtro é o que mantém a frase verdadeira nos dois
  // ramos. Sem filtro ativo nada é aguardado, e o foco continua indo para a
  // mensagem dentro do próprio clique.
  if (isFiltering.value && isLoaded(id)) {
    resetFilters();
    await nextTick();
  }

  const element = /^\d+$/.test(id)
    ? threadRef.value?.querySelector(`[data-message-id="${id}"]`)
    : null;

  if (!element) {
    useAlert(t('KANBAN.SUMMARY.MESSAGE_NOT_LOADED'));
    return;
  }

  // tabindex -1 + focus: leva também o foco do teclado até a mensagem, em vez
  // de só rolar a tela para quem enxerga. Continua dentro do diálogo, então a
  // armadilha de foco segue válida.
  element.setAttribute('tabindex', '-1');
  element.focus({ preventScroll: true });
  // jsdom não implementa scrollIntoView; a chamada opcional mantém o caminho
  // de foco testável sem um stub global de ambiente.
  element.scrollIntoView?.({ block: 'center' });
};
</script>

<template>
  <TeleportWithDirection to="body">
    <!--
      `fixed inset-0`: a sobreposição não participa do fluxo do documento e
      por isso não entra em documentElement.scrollWidth — a mesma medida que
      o `.sr-only` sem ancestral posicionado estourava (1.4.10 Reflow). Não
      há nenhum .sr-only aqui, e não deve haver: se algum for necessário,
      precisa de ancestral `relative`.
    -->
    <div
      class="fixed inset-0 z-50 flex items-center justify-center p-4 overflow-hidden bg-n-alpha-black2"
      @click="close"
    >
      <div
        ref="dialogRef"
        role="dialog"
        aria-modal="true"
        :aria-labelledby="titleId"
        tabindex="-1"
        data-testid="kanban-conversation-modal"
        class="flex flex-col w-full max-w-2xl min-h-0 overflow-hidden border shadow-xl outline-none max-h-[88vh] rounded-xl border-n-weak bg-n-solid-1 lg:flex-row"
        :class="dialogWidthClass"
        @click.stop
      >
        <!--
        ABAIXO de lg, esta coluna é um item flex de `dialogRef` que pode ser
        encolhido até 0 (`min-h-0`) pelo algoritmo de flex-shrink — mas os
        FILHOS dela (cabeçalho, filtros, `kanban-debthub-panel`, resumo) têm
        `min-height: auto` e não encolhem: medido, a soma deles (458px em
        360px de largura) passa por cima do espaço que a coluna recebeu
        (382px). Sem `overflow-y-auto` aqui, esse excesso vaza da própria
        caixa (`overflow: visible` por padrão) e pinta por cima do painel de
        detalhe, que é o próximo irmão. Com `overflow-y-auto`, o excesso vira
        rolagem CONTIDA nesta caixa em vez de vazamento visual — em >=lg isso
        não muda nada (conteúdo já cabe, medido, sem barra de rolagem).
      -->
        <div
          class="flex flex-col flex-1 min-w-0 min-h-[9rem] overflow-y-auto overflow-x-hidden lg:order-2 lg:min-h-0"
        >
          <div
            class="flex items-start justify-between gap-3 px-4 py-3 border-b shrink-0 border-n-weak"
          >
            <div class="min-w-0">
              <!--
              h2, não h3: o diálogo é teleportado para o fim do <body>, então
              na ordem do documento ele vem depois dos h3 dos cards — subir
              de nível é permitido, pular para baixo não seria (heading-order).
            -->
              <h2
                :id="titleId"
                class="m-0 text-base font-semibold truncate text-n-slate-12"
                :title="identity.title"
              >
                {{ identity.title }}
              </h2>
              <p v-if="identity.phone" class="m-0 text-xs text-n-slate-11">
                {{ identity.phone }}
              </p>
            </div>

            <div class="flex items-center gap-1 shrink-0">
              <a
                :href="conversationPath"
                class="px-2 py-1 text-xs font-medium rounded-lg text-n-slate-12 bg-n-alpha-2 hover:bg-n-alpha-3"
                @click="onOpenFullConversation"
              >
                {{ t('KANBAN.BOARD.CONVERSATION_MODAL.OPEN_FULL') }}
              </a>
              <NextButton
                slate
                ghost
                xs
                icon="i-lucide-x"
                data-testid="kanban-conversation-modal-close"
                :aria-label="t('KANBAN.BOARD.CONVERSATION_MODAL.CLOSE')"
                @click="close"
              />
            </div>
          </div>

          <!--
          Busca e filtro ficam FORA da área que rola: filtrar e perder de
          vista o próprio filtro (e a linha que diz o alcance dele) é como se
          perde a noção do que se está olhando. Só existem quando há mensagem
          carregada — sobre zero mensagem os dois controles não têm o que
          fazer, e os estados de carga/erro/vazio ficam limpos.
        -->
          <div
            v-if="loadedCount > 0"
            class="flex flex-col gap-1.5 px-4 py-2 border-b shrink-0 border-n-weak"
          >
            <div class="flex flex-wrap items-center gap-2">
              <!--
              `reset-base` pelo mesmo motivo do campo de busca do board:
              #{$form-input-selector} de _base.scss aplica field-base h-10 por
              cima de qualquer input[type], e o wrapper `relative` herdaria os
              40px reais, descentralizando a lupa em 8px.
            -->
              <div class="relative flex-1 min-w-[180px]">
                <span
                  class="i-lucide-search absolute left-2 top-1/2 -translate-y-1/2 size-3.5 text-n-slate-10"
                  aria-hidden="true"
                />
                <input
                  v-model="searchQuery"
                  type="text"
                  data-testid="kanban-conversation-modal-search"
                  class="reset-base w-full h-8 pl-7 pr-2 text-sm rounded-lg border border-n-weak bg-n-background text-n-slate-12 focus:border-n-brand"
                  :aria-label="
                    t('KANBAN.BOARD.CONVERSATION_MODAL.SEARCH_LABEL')
                  "
                  :placeholder="
                    t('KANBAN.BOARD.CONVERSATION_MODAL.SEARCH_PLACEHOLDER')
                  "
                />
              </div>

              <!--
              <select> nativo, e não o popover dos filtros do board: o popover
              teria de viver dentro da armadilha de foco deste diálogo, e um
              único controle com teclado nativo é uma parada de Tab em vez de
              seis. `!h-8 !mb-0` desfaz o field-base h-10/mb-4 que _base.scss
              aplica em TODO select (a regra do select não tem escape por
              reset-base), o mesmo override já usado em
              SearchDateRangeSelector.vue.
            -->
              <select
                v-model="attachmentFilter"
                data-testid="kanban-conversation-modal-attachment-filter"
                class="!h-8 !mb-0 !py-1 shrink-0 w-auto text-sm"
                :aria-label="
                  t('KANBAN.BOARD.CONVERSATION_MODAL.ATTACHMENT_FILTER_LABEL')
                "
              >
                <option
                  v-for="option in ATTACHMENT_FILTER_OPTIONS"
                  :key="option"
                  :value="option"
                >
                  {{
                    t(
                      `KANBAN.BOARD.CONVERSATION_MODAL.ATTACHMENT_FILTER.${option.toUpperCase()}`
                    )
                  }}
                </option>
              </select>
            </div>

            <!--
            4.1.3: filtrar troca a lista inteira sem mover o foco. Sem região
            de status, quem usa leitor de tela digita e não recebe nada — a
            contagem e o ALCANCE da busca são anunciados aqui.
          -->
            <p
              role="status"
              data-testid="kanban-conversation-modal-thread-status"
              class="m-0 text-xs text-n-slate-11"
            >
              {{ threadStatusLabel }}
            </p>
          </div>

          <!--
          As duas superfícies que diferenciam esta ferramenta vivem AQUI, FORA
          do contêiner que rola, e não numa tela nova: a correlação com o
          DEBTHUB e o resumo respondem sobre ESTA conversa, no momento em que
          ela é aberta. A correlação resolve sincronamente contra outro
          sistema, então montá-la aqui (uma conversa por vez) é também o que a
          mantém fora do caminho de render do board.

          FORA do scroll por defeito medido: enquanto estavam DENTRO dele, o
          `scrollToBottom` do mount (que existe para abrir na mensagem mais
          recente, a que motivou o clique) empurrava os dois para fora da
          vista. O agente precisava rolar o histórico inteiro de volta ao topo
          para ver se havia correlação e para achar o botão de resumo -- ou
          seja, as duas features ficavam invisíveis justamente na abertura,
          que é quando ele decide o que fazer com a conversa.
        -->
          <KanbanDebthubLinkPanel
            :conversation-display-id="conversation.id"
            :detail-open-key="detailOpenKey"
            @view-details="openDetail"
            @invalidate-details="closeDetail"
          />
          <!-- GATILHO, e não o resumo inteiro: o conteúdo mora no painel da
               esquerda. Aqui fica só a porta, que não disputa altura com a
               conversa. -->
          <div class="flex px-4 py-2 border-b shrink-0 border-n-weak">
            <NextButton
              slate
              faded
              sm
              icon="i-lucide-sparkles"
              data-testid="kanban-summary-open"
              :label="t('KANBAN.SUMMARY.OPEN')"
              :aria-expanded="isSummaryOpen ? 'true' : 'false'"
              aria-controls="kanban-summary-panel"
              @click="toggleSummary"
            />
          </div>

          <!--
          `min-h-32` (128px) em vez de `min-h-0`: sem piso, o flex-shrink
          zera esta caixa antes de tocar nos irmãos de `min-height: auto`
          (ver comentário da coluna principal, acima) — medido: a conversa
          inteira sumia (thread 0px) exatamente no viewport onde o painel de
          detalhe está aberto. O piso não altera o caso comum (>=lg e a
          maioria dos viewports abaixo de lg já dão mais que 128px de sobra,
          medido) — ele só entra em jogo quando o espaço é insuficiente, e
          nesse caso a rolagem própria da coluna (acima) alcança o restante.
        -->
          <div
            ref="scrollRef"
            data-testid="kanban-conversation-modal-scroll"
            class="flex-1 min-h-32 overflow-y-auto"
            @scroll="onThreadScroll"
          >
            <div ref="threadRef" class="py-3">
              <p
                v-if="isLoading"
                role="status"
                class="m-0 px-4 py-6 text-sm text-center text-n-slate-11"
              >
                {{ t('KANBAN.BOARD.CONVERSATION_MODAL.LOADING') }}
              </p>

              <div
                v-else-if="hasError"
                role="alert"
                class="flex flex-col items-center gap-2 px-4 py-6 text-center"
              >
                <p class="m-0 text-sm text-n-slate-11">
                  {{ t('KANBAN.BOARD.CONVERSATION_MODAL.ERROR') }}
                </p>
                <NextButton
                  slate
                  faded
                  xs
                  :label="t('KANBAN.BOARD.CONVERSATION_MODAL.RETRY')"
                  @click="loadLatest"
                />
              </div>

              <p
                v-else-if="loadedCount === 0"
                role="status"
                class="m-0 px-4 py-6 text-sm text-center text-n-slate-11"
              >
                {{ t('KANBAN.BOARD.CONVERSATION_MODAL.EMPTY') }}
              </p>

              <template v-else>
                <!--
                Topo do histórico. A página vazia é EXPLÍCITA: sem isso,
                "não voltou nada" e "não carregou nada ainda" viram a mesma
                tela. O texto (HISTORY_START) não afirma que o histórico
                acabou — só que nada voltou por este caminho (ver o
                comentário de hasReachedStart, acima).
              -->
                <div class="flex flex-col items-center gap-1 px-4 pb-3">
                  <p
                    v-if="hasReachedStart"
                    data-testid="kanban-conversation-modal-history-start"
                    class="m-0 text-xs text-n-slate-10"
                  >
                    {{ t('KANBAN.BOARD.CONVERSATION_MODAL.HISTORY_START') }}
                  </p>
                  <NextButton
                    v-else
                    slate
                    faded
                    xs
                    data-testid="kanban-conversation-modal-load-older"
                    :is-loading="isLoadingOlder"
                    :label="
                      isLoadingOlder
                        ? t('KANBAN.BOARD.CONVERSATION_MODAL.LOADING_OLDER')
                        : t('KANBAN.BOARD.CONVERSATION_MODAL.LOAD_OLDER')
                    "
                    @click="loadOlderKeepingPosition"
                  />
                  <!--
                  Falha ao carregar o trecho anterior é indistinguível de fim
                  de histórico se ficar calada, e o agente concluiria que a
                  conversa começa onde a rede falhou.
                -->
                  <p
                    v-if="hasOlderError"
                    role="alert"
                    data-testid="kanban-conversation-modal-older-error"
                    class="m-0 text-xs text-n-ruby-11"
                  >
                    {{ t('KANBAN.BOARD.CONVERSATION_MODAL.OLDER_ERROR') }}
                  </p>
                </div>

                <div
                  v-if="visibleCount === 0"
                  data-testid="kanban-conversation-modal-no-match"
                  class="flex flex-col items-center gap-2 px-4 py-6 text-center"
                >
                  <p class="m-0 text-sm text-n-slate-11">
                    {{ t('KANBAN.BOARD.CONVERSATION_MODAL.NO_MATCH') }}
                  </p>
                  <NextButton
                    slate
                    faded
                    xs
                    data-testid="kanban-conversation-modal-clear-filters"
                    :label="t('KANBAN.BOARD.CONVERSATION_MODAL.CLEAR_FILTERS')"
                    @click="resetFilters"
                  />
                </div>

                <!--
                MessageList é do upstream e resolve a prévia do in-reply-to
                dentro da lista que recebe (depois na store da conversa
                selecionada, que nesta rota é `{}`). Com filtro ativo, a
                mensagem respondida pode estar escondida e a prévia degrada
                para não resolvida — sem erro, e o filtro é do próprio agente.
              -->
                <MessageList
                  v-else
                  :messages="visibleMessages"
                  :current-user-id="currentUserId"
                  :is-an-email-channel="isEmailChannel"
                />
              </template>
            </div>
          </div>
        </div>

        <!--
        Largura MEDIDA, não suposta (1rem = 16px, sem override de
        font-size na raiz — conferido em _next-colors.scss e correlatos):
        fechado, o diálogo permanece em max-w-2xl (672px), sem regressão.
        Aberto, em >=lg (1024px) ele vira lg:flex-row com teto lg:max-w-5xl
        (1024px); o painel é lg:w-[21rem] (336px) e o conteúdo principal
        absorve o resto via flex-1. No PIOR caso dentro do breakpoint —
        viewport exatamente 1024px, os 32px de padding do overlay tirados —
        o diálogo cabe em 992px e o conteúdo principal fica com 992-336=656px.
        A partir de xl o painel vai a 25rem (400px) E o teto do diálogo sobe
        para max-w-6xl (1152px): os 64px a mais do painel são pagos pelo
        DIÁLOGO, não pela conversa, que fica com 1152-400=752px. Sem o
        xl:max-w-6xl a conversa PERDIA 32px ao passar de 1024 para 1280 —
        medido pela revisão de frontend em 2026-08-20 (654px em 1024 contra
        622px em 1280), que é o inverso do que esta seção promete.
        Em 1280px (o corte de 5 colunas do board, medido à parte) sobram
        1280-32-1152=96px de folga: sem corte.
        ABAIXO de lg o layout continua flex-col (empilhado, decisão
        deliberada e não "sobrepor"): o painel vem DEPOIS do conteúdo
        principal, com altura própria (`h-64`, fixa) e não disputa largura
        com a conversa numa tela que já não tem 1024px sobrando. Diferente
        de >=lg, aqui a largura NÃO é o problema — é a ALTURA: `h-64` fixo
        (mantido: a medição abaixo é de altura, e ela não mudou quando a
        largura em >=lg passou de `w-80` para `w-[21rem]`/`xl:w-[25rem]`)
        mais o conteúdo não-encolhível da coluna principal (cabeçalho,
        filtros, `kanban-debthub-panel`, resumo — 458px medidos em 360px de
        largura) somam mais do que os 80vh do diálogo permitem. A coluna
        principal resolve isso com rolagem própria (ver comentário dela,
        acima); este painel resolve com a rolagem própria que cada aba já
        tem internamente (`overflow-y-auto` por `tabpanel`).
      -->
        <!-- ESQUERDA no >=lg (`lg:order-1`, contra `lg:order-2` da coluna
             principal e `lg:order-3` do painel de detalhe); ABAIXO da conversa
             no modo estreito, onde a ordem do DOM vale e a conversa vem
             primeiro — mesma decisão do painel de detalhe, pelo mesmo motivo:
             quem abriu num viewport apertado não pode perder a thread de vista. -->
        <KanbanConversationSummary
          v-if="isSummaryOpen"
          :conversation-display-id="conversation.id"
          class="flex flex-col shrink border-t border-n-weak h-[30vh] min-h-[6rem] max-h-64 lg:order-1 lg:h-auto lg:min-h-0 lg:max-h-none lg:shrink-0 lg:w-80 xl:w-[25rem] lg:border-t-0 lg:border-r"
          @go-to-message="goToCitedMessage"
          @close="isSummaryOpen = false"
        />

        <KanbanDebthubDetailPanel
          v-if="detailRequest"
          :subject="detailRequest.subject"
          :kind="detailRequest.kind"
          :conversation-display-id="conversation.id"
          class="flex flex-col shrink border-t border-n-weak h-[30vh] min-h-[6rem] max-h-64 lg:order-3 lg:h-auto lg:min-h-0 lg:max-h-none lg:shrink-0 lg:w-80 xl:w-[25rem] lg:border-t-0 lg:border-l"
          @close="closeDetail"
        />
      </div>
    </div>
  </TeleportWithDirection>
</template>
